// ✅ THEME + UX FIXES (NO business/API logic changes)
// FIX 1: Rounded corners for ALL tables (outer + nested) even when horizontally scrollable
// FIX 2: Clip header corners properly (no square blue header corners)
// FIX 3: Keep your existing theme intact (Orange header, blue surfaces, zebra rows)

import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Box,
  Paper,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Collapse,
  Typography,
  LinearProgress,
  Snackbar,
  Alert,
  Chip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  Stack,
  Divider,
} from "@mui/material";

import { API_URL, API_UPLOAD_URL } from "./config";

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro", "Helvetica Neue", Arial, sans-serif';

export default function StudentAttendanceReport() {
  const token = localStorage.getItem("auth");

  // ✅ Theme constants (same palette as above pages)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  // ✅ shared sx (match ClassScheduler)
  const pageWrapSx = { p: 2, backgroundColor: BG, fontFamily: FONT_FAMILY };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
    backgroundColor: "transparent",
    fontFamily: FONT_FAMILY,
    "& .MuiTypography-root, & .MuiButton-root, & .MuiChip-root, & .MuiTableCell-root, & .MuiInputBase-root, & .MuiInputLabel-root": { fontFamily: FONT_FAMILY },
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "#fff",
    border: `1px solid ${BORDER}`,
  };

  const sectionSx = { ...surfaceSx, p: 2 };

  // ✅ INPUTS: outline + focus label orange
  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
    "& .MuiInputLabel-root.Mui-focused": { color: THEME_ORANGE },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  // ✅ Toggle buttons on orange header
  const toggleGroupSx = {
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(255,255,255,0.35)",
    backgroundColor: "rgba(255,255,255,0.12)",
    "& .MuiToggleButton-root": {
      textTransform: "none",
      fontWeight: 900,
      color: "white",
      border: "none",
      px: 1.6,
      py: 0.75,
      "&.Mui-selected": {
        backgroundColor: "rgba(255,255,255,0.24)",
        color: "white",
      },
      "&:hover": { backgroundColor: "rgba(255,255,255,0.18)" },
    },
  };

  // ✅ FIX: Fully-rounded tables even with horizontal scroll
  // - Outer Paper keeps borderRadius + border
  // - Inner Box handles overflow-x and uses borderRadius + clip (overflow: hidden)
  // - TableContainer uses component="div" so it doesn't fight Paper radius
  const tableCardSx = {
    ...surfaceSx,
    mt: 2,
    p: 0,
    overflow: "hidden", // clips header corners
  };

  const scrollClipSx = {
    overflowX: "auto",
    overflowY: "hidden",
    borderRadius: 2,
  };

  const theadRowSx = {
    backgroundColor: "rgba(25,118,210,0.95)",
    "& th": {
      color: "#fff",
      fontWeight: 900,
      borderBottom: "1px solid rgba(255,255,255,0.18)",
      whiteSpace: "nowrap",
      py: 1,
    },
  };


  const rowNumberSx = {
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    minWidth: 34, height: 28, px: 1, borderRadius: 999,
    color: "rgba(25,118,210,0.95)", backgroundColor: "rgba(25,118,210,0.10)",
    border: "1px solid rgba(25,118,210,0.20)", fontWeight: 900,
  };

  const progressSx = {
    height: 8,
    borderRadius: 999,
    backgroundColor: "rgba(25,118,210,0.10)",
    "& .MuiLinearProgress-bar": {
      backgroundColor: THEME_ORANGE,
      borderRadius: 999,
    },
  };

  const chipSx = {
    borderRadius: 2,
    backgroundColor: "rgba(25,118,210,0.06)",
    border: "1px solid rgba(25,118,210,0.18)",
    fontWeight: 800,
  };

  // ✅ Autocomplete: chips + icons match theme
  const autoCompleteSx = {
    ...inputSx,
    "& .MuiAutocomplete-tag": {
      borderRadius: 8,
      backgroundColor: "rgba(25,118,210,0.06)",
      border: "1px solid rgba(25,118,210,0.18)",
      fontWeight: 800,
    },
    "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
      color: "rgba(25,118,210,0.75)",
      "&:hover": { backgroundColor: "rgba(25,118,210,0.06)" },
    },
  };

  const collapseSurfaceSx = {
    ...surfaceSx,
    p: 1.5,
    borderColor: "rgba(25,118,210,0.16)",
  };

  // ===== state (business logic unchanged) =====
  const [campusCode, setCampusCode] = useState("");
  const [username, setUsername] = useState("");
  const [loadingLookups, setLoadingLookups] = useState(false);

  const [viewMode, setViewMode] = useState("day"); // "class" | "day"

  const [filters, setFilters] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    AcademicYear: [],
    StudentID: [],
  });
  const [studentIdInput, setStudentIdInput] = useState("");

  const [options, setOptions] = useState({
    SubCampusCode: [],
    DegreeCode: [],
    BranchCode: [],
    SemesterCode: [],
    AcademicYear: [],
  });

  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryRows, setSummaryRows] = useState([]);
  const [openSemesters, setOpenSemesters] = useState({});
  const [subjectData, setSubjectData] = useState({});

  const [loadingDay, setLoadingDay] = useState(false);
  const [dayRows, setDayRows] = useState([]);
  const [openDayKeys, setOpenDayKeys] = useState({});
  const [dayDates, setDayDates] = useState({});

  const [toast, setToast] = useState({ open: false, type: "success", msg: "" });

  // Date helper (IST)
  const convertToISTDate = (utcTime) =>
    utcTime
      ? new Date(utcTime).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" })
      : "";

  useEffect(() => {
    if (!token) return;
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      setCampusCode(payload.campusCode || payload.CampusCode || "");
      setUsername(payload.username || payload.name || "");
    } catch (e) {
      console.error("Failed to decode token", e);
      setToast({ open: true, type: "error", msg: "Invalid session token." });
    }
  }, [token]);

  const authHeader = useMemo(
    () => ({ headers: { Authorization: `Bearer ${token}` } }),
    [token]
  );

  useEffect(() => {
    if (!campusCode) return;
    let cancelled = false;

    (async () => {
      try {
        setLoadingLookups(true);
        const { data } = await axios.get(`${API_URL}/api/lookups/all`, {
          ...authHeader,
          params: { CampusCode: campusCode },
        });
        if (cancelled) return;
        setOptions({
          SubCampusCode: data?.SubCampusCode || [],
          DegreeCode: data?.DegreeCode || [],
          BranchCode: data?.BranchCode || [],
          SemesterCode: (data?.SemesterCode || []).map(String),
          AcademicYear: data?.AcademicYear || [],
        });
      } catch (e) {
        console.error(e);
        setToast({ open: true, type: "error", msg: "Failed to load dropdowns." });
      } finally {
        if (!cancelled) setLoadingLookups(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [campusCode, authHeader]);

  // ===== Helpers =====
  const pct = (val) => {
    const num = Number(val);
    if (!Number.isFinite(num)) return 0;
    return Math.max(0, Math.min(100, num));
  };

  const parseIDs = (txt) =>
    Array.from(
      new Set(
        (txt || "")
          .split(/[\s,]+/g)
          .map((s) => s.trim())
          .filter(Boolean)
      )
    );

  const hasAnyFilter = () =>
    (filters.StudentID?.length ?? 0) > 0 ||
    (filters.SubCampusCode?.length ?? 0) > 0 ||
    (filters.DegreeCode?.length ?? 0) > 0 ||
    (filters.BranchCode?.length ?? 0) > 0 ||
    (filters.SemesterCode?.length ?? 0) > 0 ||
    (filters.AcademicYear?.length ?? 0) > 0;

  const buildParams = (extra = {}) => {
    const p = { CampusCode: campusCode || undefined, ...extra };
    if (filters.SubCampusCode.length) p.SubCampusCode = filters.SubCampusCode;
    if (filters.DegreeCode.length) p.DegreeCode = filters.DegreeCode;
    if (filters.BranchCode.length) p.BranchCode = filters.BranchCode;
    if (filters.SemesterCode.length) p.SemesterCode = filters.SemesterCode;
    if (filters.AcademicYear.length) p.AcademicYear = filters.AcademicYear;
    if (filters.StudentID.length) p.StudentID = filters.StudentID;
    return p;
  };

  // ===== Class View handlers =====
  const fetchSummary = async () => {
    if (!hasAnyFilter()) {
      return setToast({
        open: true,
        type: "error",
        msg: "Select at least one filter (IDs or any code).",
      });
    }
    setLoadingSummary(true);
    setSummaryRows([]);
    setOpenSemesters({});
    setSubjectData({});
    try {
      const { data } = await axios.get(`${API_URL}/api/attendance/summary`, {
        ...authHeader,
        params: buildParams(),
      });
      setSummaryRows(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setToast({
          open: true,
          type: "info",
          msg: "No attendance found for the selected filters.",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({ open: true, type: "error", msg: "Failed to fetch summary." });
    } finally {
      setLoadingSummary(false);
    }
  };

  const classKey = (r) =>
    [r.StudentID, r.SubCampusCode, r.DegreeCode, r.BranchCode, r.Semester].join("|");

  const toggleSemester = async (row) => {
    const key = classKey(row);
    const currentlyOpen = !!openSemesters[key];
    setOpenSemesters((p) => ({ ...p, [key]: !currentlyOpen }));

    if (!currentlyOpen && !subjectData[key]) {
      setSubjectData((prev) => ({ ...prev, [key]: { loading: true, rows: [] } }));
      try {
        const params = {
          CampusCode: campusCode || undefined,
          StudentID: [row.StudentID],
          SubCampusCode: [row.SubCampusCode],
          DegreeCode: [row.DegreeCode],
          BranchCode: [row.BranchCode],
          SemesterCode: [String(row.Semester)],
          AcademicYear: filters.AcademicYear,
        };
        const { data } = await axios.get(`${API_URL}/api/attendance/subjects`, {
          ...authHeader,
          params,
        });
        setSubjectData((prev) => ({
          ...prev,
          [key]: { loading: false, rows: Array.isArray(data) ? data : [] },
        }));
      } catch (err) {
        console.error(err);
        setSubjectData((prev) => ({ ...prev, [key]: { loading: false, rows: [] } }));
        setToast({
          open: true,
          type: "error",
          msg: `Failed to load subjects for ${row.StudentID} • ${row.SubCampusCode} • Sem ${row.Semester}.`,
        });
      }
    }
  };

  // ===== Day View handlers =====
  const dayKey = (r) =>
    [r.StudentID, r.SubCampusCode, r.DegreeCode, r.BranchCode, r.Semester].join("|");

  const fetchDaySummary = async () => {
    if (!hasAnyFilter()) {
      return setToast({
        open: true,
        type: "error",
        msg: "Select at least one filter (IDs or any code).",
      });
    }
    setLoadingDay(true);
    setDayRows([]);
    setOpenDayKeys({});
    setDayDates({});
    try {
      const { data } = await axios.get(`${API_URL}/api/attendance/day/summary`, {
        ...authHeader,
        params: buildParams(),
      });
      setDayRows(Array.isArray(data) ? data : []);
      if (!Array.isArray(data) || data.length === 0) {
        setToast({
          open: true,
          type: "info",
          msg: "No day-wise summary found for the selected filters.",
        });
      }
    } catch (e) {
      console.error(e);
      setToast({ open: true, type: "error", msg: "Failed to fetch day summary." });
    } finally {
      setLoadingDay(false);
    }
  };

  const toggleDayRow = async (row) => {
  const key = dayKey(row);
  const nextOpen = !openDayKeys[key];
  setOpenDayKeys((p) => ({ ...p, [key]: nextOpen }));

  if (nextOpen && !dayDates[key]) {
    setDayDates((p) => ({ ...p, [key]: { loading: true, rows: [] } }));

    try {
      const params = {
        CampusCode: campusCode || undefined,
        StudentID: [row.StudentID],
        SubCampusCode: [row.SubCampusCode],
        DegreeCode: [row.DegreeCode],
        BranchCode: [row.BranchCode],
        SemesterCode: [String(row.Semester)],
        AcademicYear: filters.AcademicYear,
      };

      const { data } = await axios.get(`${API_URL}/api/attendance/day/dates`, {
        ...authHeader,
        params,
      });

      setDayDates((p) => ({
        ...p,
        [key]: { loading: false, rows: Array.isArray(data) ? data : [] },
      }));
    } catch (e) {
      console.error(e);
      setDayDates((p) => ({ ...p, [key]: { loading: false, rows: [] } }));
      setToast({ open: true, type: "error", msg: "Failed to fetch per-day rows." });
    }
  }
};

  const onSearch = () => {
    if (viewMode === "class") fetchSummary();
    else fetchDaySummary();
  };

  // ===== Shared row sx =====
  const zebraRowSx = (i) => ({
    backgroundColor: i % 2 === 0 ? ROW_ALT : "#FFFFFF",
    cursor: "pointer",
    "&:hover": { backgroundColor: ROW_HOVER },
    "& td": { borderBottom: "1px solid rgba(25,118,210,0.10)", py: 1 },
  });

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Student Attendance Report
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Filter by campus codes / IDs and view class-wise or day-wise attendance
            </Typography>
          </Box>

          <ToggleButtonGroup
            size="small"
            value={viewMode}
            exclusive
            onChange={(_, v) => v && setViewMode(v)}
            sx={toggleGroupSx}
          >
            <ToggleButton value="day">Day View</ToggleButton>
            <ToggleButton value="class">Class View</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <Box sx={bodySx}>
          {/* ✅ Filters section with left stripe */}
          <Paper variant="outlined" sx={sectionSx}>
            <Box sx={{ pl: 1 }}>
              <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                Filters
              </Typography>
              <Divider sx={{ mb: 2, borderColor: "rgba(25,118,210,0.12)" }} />

              {/* Dropdown filters */}
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "repeat(5, 1fr)" },
                  gap: 1,
                  mb: 2,
                }}
              >
                <Autocomplete
                  multiple
                  options={options.SubCampusCode}
                  value={filters.SubCampusCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, SubCampusCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SubCampusCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.DegreeCode}
                  value={filters.DegreeCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, DegreeCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="DegreeCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.BranchCode}
                  value={filters.BranchCode}
                  onChange={(_, value) => setFilters((p) => ({ ...p, BranchCode: value }))}
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="BranchCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.AcademicYear}
                  value={filters.AcademicYear}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, AcademicYear: value }))
                  }
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="AcademicYear(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                <Autocomplete
                  multiple
                  options={options.SemesterCode}
                  value={filters.SemesterCode}
                  onChange={(_, value) =>
                    setFilters((p) => ({ ...p, SemesterCode: value.map(String) }))
                  }
                  disableCloseOnSelect
                  filterSelectedOptions
                  openOnFocus
                  loading={loadingLookups}
                  sx={autoCompleteSx}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="SemesterCode(s)"
                      size="small"
                      sx={inputSx}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {loadingLookups ? <CircularProgress size={16} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />
              </Box>

              {/* StudentID multi-input */}
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{ mb: 1, alignItems: "flex-start" }}
              >
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Student IDs (comma / space / newline)"
                    placeholder="e.g. STUID01, STUID02"
                    value={studentIdInput}
                    onChange={(e) => {
                      const txt = e.target.value;
                      setStudentIdInput(txt);
                      const ids = parseIDs(txt);
                      setFilters((p) => ({ ...p, StudentID: ids }));
                    }}
                    sx={inputSx}
                  />

                  {filters.StudentID.length > 0 && (
                    <Stack direction="row" spacing={1} mt={1} flexWrap="wrap" useFlexGap>
                      {filters.StudentID.map((id) => (
                        <Chip
                          key={id}
                          size="small"
                          label={id}
                          onDelete={() => {
                            const next = filters.StudentID.filter((x) => x !== id);
                            setFilters((p) => ({ ...p, StudentID: next }));
                            setStudentIdInput(next.join(", "));
                          }}
                          sx={{ ...chipSx, mb: 1 }}
                        />
                      ))}
                    </Stack>
                  )}
                </Box>

                {/* Search / Clear */}
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Button
                    variant="contained"
                    sx={primaryBtnSx}
                    onClick={onSearch}
                    disabled={(viewMode === "class" && loadingSummary) || (viewMode === "day" && loadingDay)}
                  >
                    {(viewMode === "class" ? loadingSummary : loadingDay) ? "Loading…" : "Search"}
                  </Button>

                  <Button
                    variant="outlined"
                    sx={outlinedBtnSx}
                    onClick={() => {
                      setFilters({
                        SubCampusCode: [],
                        DegreeCode: [],
                        BranchCode: [],
                        SemesterCode: [],
                        AcademicYear: [],
                        StudentID: [],
                      });
                      setStudentIdInput("");
                      setSummaryRows([]);
                      setDayRows([]);
                      setOpenSemesters({});
                      setSubjectData({});
                      setOpenDayKeys({});
                      setDayDates({});
                    }}
                  >
                    Clear Filters
                  </Button>
                </Box>
              </Stack>
            </Box>
          </Paper>

          {/* ===== CLASS VIEW ===== */}
          {viewMode === "class" && (
            // ✅ Rounded wrapper like your "below theme" (Paper + inner scroll clip)
            <Paper elevation={0} sx={tableCardSx}>
              <Box sx={scrollClipSx}>
                <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 48 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>TotalClasses</TableCell>
                        <TableCell>Attended</TableCell>
                        <TableCell>Absent</TableCell>
                        <TableCell>Attendance %</TableCell>
                        <TableCell>AttendanceFine</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingSummary ? (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                              <CircularProgress size={20} />
                              <Typography>Loading summary…</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : summaryRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Typography sx={{ p: 2, opacity: 0.8 }}>No data.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        summaryRows.map((row, idx) => {
                          const key = [
                            row.StudentID,
                            row.SubCampusCode,
                            row.DegreeCode,
                            row.BranchCode,
                            row.Semester,
                          ].join("|");
                          const isOpen = !!openSemesters[key];
                          const subj = subjectData[key];

                          return (
                            <React.Fragment key={`${key}-${idx}`}>
                              <TableRow sx={zebraRowSx(idx)} onClick={() => toggleSemester(row)}>
                                <TableCell><Box component="span" sx={rowNumberSx}>{idx + 1}</Box></TableCell>
                                <TableCell>{row.StudentID}</TableCell>
                                <TableCell>{row.SubCampusCode}</TableCell>
                                <TableCell>{row.DegreeCode}</TableCell>
                                <TableCell>{row.BranchCode}</TableCell>
                                <TableCell>{row.Semester}</TableCell>
                                <TableCell>{Number(row.TotalClasses || 0)}</TableCell>
                                <TableCell>{Number(row.AttendedClasses || 0)}</TableCell>
                                <TableCell>{Number(row.AbsentClasses || 0)}</TableCell>
                                <TableCell sx={{ minWidth: 180 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ minWidth: 64, fontWeight: 800 }}>
                                      {pct(row.AttendancePercentage).toFixed(2)}%
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct(row.AttendancePercentage)}
                                      sx={{ ...progressSx, width: 110 }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>{Number(row.AttendanceFine || 0)}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Box sx={{ m: 1 }}>
                                      <Box sx={collapseSurfaceSx}>
                                        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                                          Attendance By Subject • {row.StudentID} (Sem {row.Semester})
                                        </Typography>
                                        <Divider sx={{ mb: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />

                                        {subj?.loading ? (
                                          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 2 }}>
                                            <CircularProgress size={18} />
                                            <Typography>Loading subjects…</Typography>
                                          </Box>
                                        ) : (subj?.rows || []).length === 0 ? (
                                          <Typography sx={{ p: 2, opacity: 0.8 }}>No subject details.</Typography>
                                        ) : (
                                          // ✅ Nested table: also rounded (Paper + inner scroll clip)
                                          <Paper elevation={0} sx={{ ...tableCardSx, mt: 0 }}>
                                            <Box sx={scrollClipSx}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow
                                                    sx={{
                                                      backgroundColor: "rgba(25,118,210,0.10)",
                                                      "& th": { fontWeight: 900, py: 1 },
                                                    }}
                                                  >
                                                    <TableCell>StudentID</TableCell>
                                                    <TableCell>Subject</TableCell>
                                                    <TableCell>SubjectName</TableCell>
                                                    <TableCell>Section</TableCell>
                                                    <TableCell>TeacherName</TableCell>
                                                    <TableCell>Scheduled</TableCell>
                                                    <TableCell>Attended</TableCell>
                                                    <TableCell>Absent</TableCell>
                                                    <TableCell>Attendance %</TableCell>
                                                    <TableCell>AttendanceFine</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {(subj?.rows || []).map((sRow, sIdx) => (
                                                    <TableRow
                                                      key={`${row.StudentID}-${row.Semester}-sub-${sIdx}`}
                                                      sx={{
                                                        backgroundColor:
                                                          sIdx % 2 === 0 ? "rgba(25,118,210,0.04)" : "#fff",
                                                        "& td": {
                                                          borderBottom: "1px solid rgba(25,118,210,0.10)",
                                                          py: 1,
                                                        },
                                                      }}
                                                    >
                                                      <TableCell>{sRow.StudentID}</TableCell>
                                                      <TableCell>{sRow.SubjectCode}</TableCell>
                                                      <TableCell>{sRow.SubjectNameCode}</TableCell>
                                                      <TableCell>{sRow.Section}</TableCell>
                                                      <TableCell>{sRow.TeacherName}</TableCell>
                                                      <TableCell>{Number(sRow.TotalClasses || 0)}</TableCell>
                                                      <TableCell>{Number(sRow.AttendedClasses || 0)}</TableCell>
                                                      <TableCell>{Number(sRow.AbsentClasses || 0)}</TableCell>
                                                      <TableCell sx={{ minWidth: 180 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                          <Box sx={{ minWidth: 64, fontWeight: 800 }}>
                                                            {pct(sRow.AttendancePercentage).toFixed(2)}%
                                                          </Box>
                                                          <LinearProgress
                                                            variant="determinate"
                                                            value={pct(sRow.AttendancePercentage)}
                                                            sx={{ ...progressSx, width: 110 }}
                                                          />
                                                        </Box>
                                                      </TableCell>
                                                      <TableCell>{Number(sRow.AttendanceFine || 0)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Paper>
                                        )}
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}

          {/* ===== DAY VIEW ===== */}
          {viewMode === "day" && (
            // ✅ Rounded wrapper like your "below theme" (Paper + inner scroll clip)
            <Paper elevation={0} sx={tableCardSx}>
              <Box sx={scrollClipSx}>
                <TableContainer component="div">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={theadRowSx}>
                        <TableCell sx={{ width: 48 }}>#</TableCell>
                        <TableCell>StudentID</TableCell>
                        <TableCell>SubCampus</TableCell>
                        <TableCell>Degree</TableCell>
                        <TableCell>Branch</TableCell>
                        <TableCell>Semester</TableCell>
                        <TableCell>WorkingDays</TableCell>
                        <TableCell>DaysAttended</TableCell>
                        <TableCell>DaysAbsent</TableCell>
                        <TableCell>Attendance %</TableCell>
                        <TableCell>AttendanceFine</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loadingDay ? (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Box sx={{ p: 2, display: "flex", gap: 1, alignItems: "center" }}>
                              <CircularProgress size={20} />
                              <Typography>Loading…</Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ) : dayRows.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={11}>
                            <Typography sx={{ p: 2, opacity: 0.8 }}>No data.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        dayRows.map((r, i) => {
                          const key = dayKey(r);
                          const isOpen = !!openDayKeys[key];
                          const dates = dayDates[key];

                          return (
                            <React.Fragment key={`${key}-${i}`}>
                              <TableRow sx={zebraRowSx(i)} onClick={() => toggleDayRow(r)}>
                                <TableCell><Box component="span" sx={rowNumberSx}>{i + 1}</Box></TableCell>
                                <TableCell>{r.StudentID}</TableCell>
                                <TableCell>{r.SubCampusCode}</TableCell>
                                <TableCell>{r.DegreeCode}</TableCell>
                                <TableCell>{r.BranchCode}</TableCell>
                                <TableCell>{r.Semester}</TableCell>
                                <TableCell>{Number(r.TotalWorkingDays || 0)}</TableCell>
                                <TableCell>{Number(r.DaysAttendedCollege || 0)}</TableCell>
                                <TableCell>{Number(r.DaysAbsent || 0)}</TableCell>
                                <TableCell sx={{ minWidth: 180 }}>
                                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <Box sx={{ minWidth: 64, fontWeight: 800 }}>
                                      {pct(r.Percent).toFixed(2)}%
                                    </Box>
                                    <LinearProgress
                                      variant="determinate"
                                      value={pct(r.Percent)}
                                      sx={{ ...progressSx, width: 110 }}
                                    />
                                  </Box>
                                </TableCell>
                                <TableCell>{Number(r.AttendanceFine || 0)}</TableCell>
                              </TableRow>

                              <TableRow>
                                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={11}>
                                  <Collapse in={isOpen} timeout="auto" unmountOnExit>
                                    <Box sx={{ m: 1 }}>
                                      <Box sx={collapseSurfaceSx}>
                                        <Typography sx={{ fontWeight: 900, color: THEME_ORANGE, mb: 1 }}>
                                          Attendance By Day • {r.StudentID} (Sem {r.Semester})
                                        </Typography>
                                        <Divider sx={{ mb: 1.5, borderColor: "rgba(25,118,210,0.12)" }} />

                                        {dates?.loading ? (
                                          <Box sx={{ p: 2, display: "flex", gap: 2, alignItems: "center" }}>
                                            <CircularProgress size={18} />
                                            <Typography>Loading days…</Typography>
                                          </Box>
                                        ) : (dates?.rows || []).length === 0 ? (
                                          <Typography sx={{ p: 2, opacity: 0.8 }}>No per-day rows.</Typography>
                                        ) : (
                                          // ✅ Nested table: also rounded (Paper + inner scroll clip)
                                          <Paper elevation={0} sx={{ ...tableCardSx, mt: 0 }}>
                                            <Box sx={scrollClipSx}>
                                              <Table size="small">
                                                <TableHead>
                                                  <TableRow
                                                    sx={{
                                                      backgroundColor: "rgba(25,118,210,0.10)",
                                                      "& th": { fontWeight: 900, py: 1 },
                                                    }}
                                                  >
                                                    <TableCell>StudentID</TableCell>
                                                    <TableCell>AttendanceDate</TableCell>
                                                    <TableCell>TotalClasses</TableCell>
                                                    <TableCell>PresentClasses</TableCell>
                                                    <TableCell>AbsentClasses</TableCell>
                                                    <TableCell>Attendance %</TableCell>
                                                    <TableCell>AttendanceFine</TableCell>
                                                  </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                  {(dates.rows || []).map((d, j) => (
                                                    <TableRow
                                                      key={`${key}-${j}`}
                                                      sx={{
                                                        backgroundColor:
                                                          j % 2 === 0 ? "rgba(25,118,210,0.04)" : "#fff",
                                                        "& td": {
                                                          borderBottom: "1px solid rgba(25,118,210,0.10)",
                                                          py: 1,
                                                        },
                                                      }}
                                                    >
                                                      <TableCell>{d.StudentID}</TableCell>
                                                      <TableCell>{convertToISTDate(d.AttendanceDate)}</TableCell>
                                                      <TableCell>{Number(d.TotalClasses || 0)}</TableCell>
                                                      <TableCell>{Number(d.PresentClasses || 0)}</TableCell>
                                                      <TableCell>{Number(d.AbsentClasses || 0)}</TableCell>
                                                      <TableCell sx={{ minWidth: 180 }}>
                                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                          <Box sx={{ minWidth: 64, fontWeight: 800 }}>
                                                            {pct(d.PresentPercentage).toFixed(2)}%
                                                          </Box>
                                                          <LinearProgress
                                                            variant="determinate"
                                                            value={pct(d.PresentPercentage)}
                                                            sx={{ ...progressSx, width: 110 }}
                                                          />
                                                        </Box>
                                                      </TableCell>
                                                      <TableCell>{Number(d.AttendanceFine || 0)}</TableCell>
                                                    </TableRow>
                                                  ))}
                                                </TableBody>
                                              </Table>
                                            </Box>
                                          </Paper>
                                        )}
                                      </Box>
                                    </Box>
                                  </Collapse>
                                </TableCell>
                              </TableRow>
                            </React.Fragment>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Paper>
          )}

          <Snackbar
            open={toast.open}
            autoHideDuration={3000}
            onClose={() => setToast((p) => ({ ...p, open: false }))}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              severity={toast.type === "error" ? "error" : toast.type === "info" ? "info" : "success"}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(25,118,210,0.18)",
              }}
              onClose={() => setToast((p) => ({ ...p, open: false }))}
            >
              {toast.msg}
            </Alert>
          </Snackbar>
        </Box>
      </Paper>
    </Box>
  );
}
